/**
 * i18n: a small locale layer for the interface chrome. A LocaleProvider resolves
 * a 'system' | explicit preference (persisted to localStorage) against
 * navigator.language, syncs <html lang>, and exposes t(key). The tables cover
 * the navigation, the home, the verdict tiers, the report section titles, and
 * the settings labels in English and German. Forensic DATA (header names, auth
 * results, RFC text) is never translated, only the interface copy around it.
 */

/**
 * GERMAN TERMINOLOGY SPINE. Decided once, in German, and applied across this
 * file, `src/next/content/*.ts` and every per-view COPY table. Do not re-derive
 * these from the English: each replaced word was a literal rendering that a
 * German reader hears in the wrong domain. `scripts/check-copy-style.mjs`
 * rule E fails the build on any of the old words, so this block is enforced,
 * not advisory. The reasoning is `docs/COPY-AUDIT.md` section 1.
 *
 *   evidence    Spuren, or Nachweis where the sense is proof rather than
 *               trace. NOT `Belege`: a Beleg is a receipt. An investigator
 *               secures Spuren, an accountant collects Belege. The verb
 *               `belegen` ("it proves") is unaffected and stays.
 *   verdict     Einstufung. NOT `Urteil`: that is a court sentence, and the
 *               product's whole thesis is that it does not pass judgment.
 *   exhibits    Abschnitte. NOT `Einträge` for this sense and never
 *               `Exponat`, a museum piece. The courtroom-exhibit metaphor
 *               does not exist in German. `Eintrag` in its ordinary sense
 *               (a DNS record, a list entry, a Registereintrag) is correct
 *               German and is left alone.
 *   field guide Handbuch, everywhere, no compounds. NOT `Feldführer` (not a
 *               German word), `Leitfaden` or `Prüfleitfaden`: three names for
 *               one surface is worse than any of them.
 *   this tool   MIME Smith, or recast the sentence. NOT `dieses Werkzeug`:
 *               software does not call itself a Werkzeug in German, that is
 *               a literal rendering of "this tool". Only the self-reference
 *               is banned. `Werkzeug` for a separate, real tool (the three
 *               MCP `Werkzeuge`, an editor you export into) and as a plain
 *               category (`ein forensisches Werkzeug`) is ordinary German
 *               and stays.
 *   legitimate  unbedenklich. NOT `seriös`, which describes people and
 *               businesses as respectable. A message is not respectable.
 *   malicious   schädlich. NOT `bösartig`, a medical or personal register
 *               (ein bösartiger Tumor, ein bösartiger Mensch).
 */

/**
 * SURFACE NAMES. One surface, one name, in each locale. A surface is named by
 * its `nav.*` label, and its own heading, every action that leads to it and
 * every sentence that points at it use that same name. Two names for one
 * surface is the defect this rule exists to prevent: the reader cannot tell
 * whether the button and the tab are the same place. Route ids are not names:
 * `#corpus`, `#triage` and `#integrations` are permanent URLs and stay.
 * `scripts/check-copy-style.mjs` rule F fails the build on the retired names,
 * and `src/next/__tests__/i18n-surface-names.test.tsx` holds the live tables
 * against the nav labels. `docs/COPY-AUDIT.md` section 6 is the evidence, and
 * it records how these five names carry the Case / Message / Evidence
 * hierarchy: the mailbox is the case file, the report is the message, the
 * report's sections are its Spuren.
 *
 *   #corpus   Mailbox / Postfach. NOT `corpus` or `Korpus` in copy: the route
 *             id kept the old word, the reader-facing name did not. Its
 *             eyebrow is the one place the Case of the hierarchy is named
 *             (case file / Fallakte).
 *   #triage   Triage in both locales. NOT `Einschätzung`, a near-homophone of
 *             `Einstufung`, which is the report's headline finding: tab and
 *             verdict then read as one word for two different things. Triage
 *             is the German the guide already uses for the activity.
 *   #guide    Field guide / Handbuch. NOT `Inspection guide`.
 *   #integrations  Channels / Kanäle. NOT `the integrations page` or
 *             `Integrationsseite`.
 *   #inspect  Inspect / Prüfen, and the rollup and index inside the mailbox
 *             keep the names their own headings carry.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Locale = "en" | "de";
export type LocalePref = Locale | "system";

export const LOCALES: { value: LocalePref; label: string }[] = [
  { value: "system", label: "System" },
  { value: "en", label: "English" },
  { value: "de", label: "Deutsch" },
];

// Exported so callers outside the React tree (e.g. ErrorBoundaryNext, which
// can't use the useT hook) read the same key instead of a duplicated literal.
export const STORAGE_KEY = "emlx:locale";
/** Pre-namespace key, read once as a fallback then migrated on write. */
const LEGACY_STORAGE_KEY = "mi.locale";

// English and German both take the singular only at exactly one. Kept as a
// function so every count-based one/many key pair picks through one rule
// rather than spelling `n === 1` at each call site.
export function pluralIsOne(_locale: Locale, n: number): boolean {
  return n === 1;
}

// Exported for the parity gate (src/next/__tests__/i18n-parity.test.ts).
// App code must go through t(); never read this table directly in components.
export const STRINGS: Record<Locale, Record<string, string>> = {
  en: {
    "nav.inspect": "Inspect",
    "nav.triage": "Triage",
    "nav.compare": "Compare",
    "nav.corpus": "Mailbox",
    "nav.rfcs": "RFCs",
    "nav.guide": "Field guide",
    "nav.settings": "Settings",
    "nav.faq": "FAQ",
    "nav.about": "About",
    "nav.integrations": "Channels",
    "nav.roadmap": "Roadmap",
    "nav.impressum": "Impressum",
    "nav.datenschutz": "Privacy",
    "app.newFile": "New file",
    "handoff.from": "Imported from {source}",
    "handoff.back": "Back to source",
    "handoff.decodeFailed": "That import link could not be decoded.",
    "app.palette": "Command palette",
    "app.modes": "Modes",
    "app.menu": "Menu",
    "app.menuAria": "Site menu",
    "toc.jump": "Jump to a section",
    "app.home": "MIME Smith: start over",
    "app.loadingView": "Loading this view",
    "app.reading": "Reading the headers",
    "app.reassembling": "Reassembling the message…",
    "app.theme": "Theme: {mode}. Click to change.",
    "app.themeTitle": "Theme: {mode} (T)",
    "cmd.groupGo": "Go",
    "cmd.groupJump": "Jump",
    "cmd.groupCompare": "Compare",
    "cmd.compareWith": "Compare with {name}",
    "cmd.groupSetting": "Setting",
    "cmd.hintSection": "section",
    "cmd.print": "Print the report",
    "cmd.groupAction": "Action",
    "cmd.groupRfc": "RFC",
    "cmd.groupSample": "Sample",
    "cmd.hintPage": "page",
    "cmd.hintIframe": "iframe",
    "cmd.hintTheme": "theme",
    "cmd.hintSpec": "spec",
    "cmd.hintLoad": "load",
    "cmd.inspectNew": "Inspect: new message",
    "cmd.inspectKw": "home start over",
    "cmd.embed": "Embed: read-only verdict panel",
    "cmd.embedKw": "embed widget iframe panel",
    "cmd.theme": "Switch theme. Currently {mode}",
    "cmd.themeKw": "dark light appearance",
    "cmd.sampleKw": "sample example message",
    "tier.legitimate": "legitimate",
    "tier.mixed": "mixed",
    "tier.suspicious": "suspicious",
    "tier.malicious": "malicious",
    "home.eyebrow": "Local email forensics · no upload",
    "home.title": "Examine the evidence in an email",
    "home.lede":
      "Open an email file or paste its raw source. MIME Smith preserves the original message while it maps headers, authentication, delivery path, body parts, links, and attachments on this device.",
    "home.choose": "Choose file…",
    "home.hint": "to inspect",
    "home.samples": "Sample messages",
    "sec.envelope": "Envelope",
    "sec.reports": "Reports",
    "sec.auth": "Authentication",
    "sec.reputation": "Reputation",
    "sec.route": "Route",
    "sec.links": "Links",
    "sec.attachments": "Attachments",
    "sec.calendar": "Calendar",
    "cal.event": "Event",
    "cal.tz": "Time zone",
    "cal.organizer": "Organizer",
    "cal.location": "Location",
    "cal.attendees": "Attendees",
    "cal.conference": "Conference link",
    "cal.vcards": "Contact cards",
    "cal.orgDiffTag": "Mismatch",
    "cal.orgDiff":
      "The invite's organizer address differs from the message's From address.",
    "sec.iocs": "Indicators",
    "sec.htmlrisks": "Active content",
    "sec.injection": "Prompt injection",
    "sec.unicode": "Unicode alerts",
    "sec.body": "Body",
    "sec.mime": "MIME tree",
    "sec.headers": "Headers",
    "sec.crypto": "Crypto",
    "sec.sandbox": "Sandbox",
    "sec.threading": "Threading",
    "sec.defects": "Defects",
    "sec.raw": "Raw",
    "set.language": "Language",
    "set.languageHint":
      "Translates the interface chrome; forensic data stays as received.",
    "set.theme": "Theme",
    "set.contents": "Contents",
    "feat.auth": "Authentication",
    "feat.authBody":
      "Read SPF, DKIM, DMARC, ARC, and BIMI evidence exactly as supplied, with clear separation between reported and locally checked results.",
    "feat.route": "Route",
    "feat.routeBody":
      "Trace the Received chain, timestamps, relay identities, and transport security in source order.",
    "feat.payload": "Message content",
    "feat.payloadBody":
      "Inspect MIME parts, rendered HTML, tracking references, links, and attachments without leaving the browser.",
    "kv.from": "From",
    "kv.to": "To",
    "kv.subject": "Subject",
    "kv.date": "Date",
    "kv.size": "Size",
    "kv.automation": "Automation",
    "kv.contentLang": "Declared language",
    "kv.bulk": "Bulk mail",
    "kv.campaign": "Campaign",
    "kv.lookalike": "Look-alike names",
    "kv.originIp": "Originating IP",
    "kv.charsets": "Charsets",
    "act.next": "Next",
    "act.triage": "Read this in plain language",
    "act.compare": "Compare with another message",
    "act.guide": "What these signals mean",
    "act.iocs": "Review the indicators",
    "act.dns": "Turn on DNS checks",
    "act.save": "Bookmark this message",
    "act.export": "Export the findings",
    "auth.relayGroup":
      "Reported by the receiving relays, copied from the headers, not re-verified here",
    "auth.localGroup":
      "Checked on this device, the local spoofing tests this tool ran itself",
    "set.eyebrow": "Preferences, stored on this device",
    "set.lead":
      "Kept in this browser. They control the display, how much an export carries, and the limits the parser works within. They do not change what leaves the device.",
    "set.themeHint": "System follows your operating-system appearance.",
    "set.textSize": "Text size",
    "set.textSizeHint": "Scales the interface only. Measurements are unaffected.",
    "set.htmlDefault": "HTML body default",
    "set.htmlDefaultHint":
      "Source shows the markup as received; rendered blocks remote content until you allow it, message by message.",
    "set.redact": "Redact exports",
    "set.redactHint": "Strips addresses and message-ids from saved reports.",
    "set.clearOnClose": "Clear on close",
    "set.clearOnCloseHint": "Drops the loaded message when the tab is closed.",
    "set.defaultDest": "Open messages in",
    "set.defaultDestHint":
      "Where a freshly opened file lands: the full report or Triage, the plain-language read.",
    "set.limitsHead": "Limits & parsing",
    "set.limitsLead":
      "Bounds for adversarial inputs and which parser stages run. These persist in this browser and apply to the next message you open.",
    "set.keyboardHead": "Keyboard",
    "set.devHead": "Developer",
    "set.devLead":
      "Your own known-bad SHA-256 denylist. The parser flags any attachment or indicator whose hash is on this list. Kept in this browser, never uploaded.",
    "set.reset": "Reset appearance & privacy",
    "opt.light": "Light",
    "opt.dark": "Dark",
    "opt.system": "System",
    "opt.compact": "Compact",
    "opt.normal": "Normal",
    "opt.comfortable": "Comfortable",
    "opt.rendered": "Rendered",
    "opt.source": "Source",
    "opt.on": "On",
    "opt.off": "Off",
    "exp.open": "Export",
    "exp.close": "Hide export options",
    "exp.groupCopy": "Copy",
    "exp.groupDownload": "Download",
    "exp.groupPrint": "Print",
    "exp.redactNote": "Applies to every export below except raw copy, STIX and print.",
    "exp.unredacted": "not redacted",
    "exp.unredactedTitle":
      "The redaction picker does not reach this one. Raw copy hands over the source byte for byte, STIX is a bundle of indicators and nothing else, and print omits sections rather than transforming data.",
    "exp.copyRaw": "copy raw",
    "exp.copyJson": "copy json",
    "exp.downloadJson": "download json",
    "exp.evidence": "evidence .zip",
    "exp.bundleUnsigned": "export unsigned",
    "exp.bundleUnsignedNote": "cannot sign here",
    "exp.bundleUnsignedToast":
      "This browser cannot sign the bundle, so it was not saved. Press the button again to take it unsigned, without a chain of custody.",
    "exp.bundleUnsignedTitle":
      "This browser cannot produce an Ed25519 signature, so the bundle would carry no chain of custody. Press again to save it unsigned, as evidence-bundle-unsigned.zip. To get a signed bundle, use a current Chrome, Safari or Firefox.",
    "exp.print": "print",
    "exp.copied": "copied",
    "exp.toolbar": "Export",
    "exp.redaction": "Redaction",
    "exp.redactNone": "No redaction",
    "exp.redactStandard": "Redact addresses",
    "exp.redactStrict": "Strict redaction",
    "exp.redactTitle":
      "Applies to every export below except raw copy, STIX and print; strict also tokenises filenames and drops body content. The subject is kept at every preset: it is usually the lure the reader is being asked to recognise.",
    "exp.htmlTitle": "Save a standalone HTML report",
    "exp.copyFailed": "Copy to the clipboard failed",
    // Every download in this product goes through one synthetic anchor click,
    // and an extension, a pop-up blocker or a window that lost focus can eat
    // it. Said once, for every save button on every surface, because the
    // reader's question is the same one each time: is the file on disk?
    "exp.downloadBlocked":
      "The browser blocked that download, so no file was saved. An extension or a lost window focus can do this.",
    "exp.retry": "retry",
    "prov.head": "Analysis provenance",
    "exp.printRedact": "redacted print",
    "exp.printRedactTitle": "Printing omits the route, the raw source and the indicators",
    "lib.clear": "clear",
    "lib.bookmarked": "Bookmarked",
    "lib.recent": "Recent",
    "lib.noSubject": "(no subject)",
    "lib.message": "message {id}",
    "a11y.removeBookmark": "Remove bookmark",
    "a11y.skip": "Skip to content",
    "exp.saved": "saved",
    "exp.cancel": "cancel",
    "exp.cancelled": "cancelled",
    "exp.cancelTitle": "Stop building the bundle",
    "exp.failed": "failed",
    "exp.sarifTitle": "SARIF 2.1.0 findings",
    "exp.stixTitle": "STIX 2.1 indicator bundle",
    "exp.compact": "compact json",
    "exp.compactTitle":
      "Minified JSON without decoded bodies; attachments become blob refs",
    "exp.groupCsv": "CSV by surface",
    "exp.csvTitle": "Download this surface as CSV",
    "exp.csvNote":
      "One file per surface, opens in a spreadsheet. RFC 4180 quoting, after a provenance block of lines beginning with #. Cells that could be read as formulas are neutralised.",
    "exp.csv.headers": "headers",
    "exp.csv.addresses": "addresses",
    "exp.csv.links": "links",
    "exp.csv.attachments": "attachments",
    "exp.csv.auth": "authentication",
    "exp.csv.indicators": "indicators",
    "exp.csv.defects": "defects",
    "exp.bundleTitle":
      "Signed evidence bundle (.zip): raw source, decoded attachments, JSON, Ed25519-signed manifest. A redaction preset withholds source and attachments and keeps their digests.",
    "bm.add": "bookmark",
    "bm.added": "bookmarked",
    "bm.addTitle": "Bookmark this message",
    "bm.removeTitle": "Remove bookmark",
    "bm.saveFailedFull":
      "This browser's storage is full, so the message was not bookmarked. Remove a bookmark and try again.",
    "bm.saveFailedNoStore":
      "This browser stores nothing for this site, so the message was not bookmarked.",
    "bm.saveFailedRefused":
      "This browser refused to store the message, so it was not bookmarked.",
    "bm.removeFailed":
      "The bookmark could not be removed. The message is still stored in this browser.",
    "vm.head": "Verification",
    "vm.lead":
      "By default only what is already in the file is read. These modes go further, each at a stated cost. They stay off until you turn one on, and the choice stays on this device.",
    "vm.parseOnly": "Parse only",
    "vm.parseOnlyDesc":
      "The default. Nothing leaves your device and no signatures are recomputed.",
    "vm.dns": "Live DNS checks",
    "vm.dnsDesc":
      "Looks up the sender's SPF, DKIM, DMARC, MX and reverse-DNS records against Cloudflare's public resolver, so a relay's reported pass can be checked instead of trusted. What this app puts in the query is the name: the sender's domain, each DKIM selector under the domain that signed (often a mail provider's, not the sender's), and the originating IP address. The request itself also shows Cloudflare your device's IP address, the way a request to any website does. The report lists the exact names before you run the lookups.",
    "vm.crypto": "Verify signatures",
    "vm.cryptoDesc":
      "Recomputes DKIM body hashes and checks S/MIME and PGP signatures with math on this device. No network.",
    "vm.sandbox": "Sandbox attachments",
    "vm.sandboxDesc":
      "Inspects risky attachments (archives, documents, HTML) inside a bounded worker, isolated from the page. No network.",
    "vm.privNet": "makes a network request",
    "vm.privCrypto": "runs crypto on device",
    "vm.privSandbox": "spins a worker",
    "vm.privNone": "stays on device",
    "vm.stamp.parse-only": "Parse only, auth relay-reported",
    "vm.stamp.crypto-enabled": "Offline, signatures checked on this device",
    "vm.stamp.sandboxed": "Sandboxed, no network",
    "vm.stampTitle": "Verification mode; change it in Settings",
    "cmd.vmode": "Verification mode: {mode}",
    "basis.head": "What this rests on",
    "basis.claim1": "1 claim",
    "basis.claimN": "{n} claims",
    "basis.kind.raw": "raw",
    "basis.kind.parsed": "from the source",
    "basis.kind.declared": "relay-reported",
    "basis.kind.inferred": "inferred here",
    "basis.kind.verified": "verified here",
    "basis.kind.enriched": "enriched",
    "basis.claim.auth-spf": "SPF: {result}",
    "basis.claim.auth-dkim": "DKIM: {result}",
    "basis.claim.auth-dmarc": "DMARC: {result}",
    "basis.claim.auth-arc-chain": "ARC chain: {status}",
    "basis.arc.valid": "valid",
    "basis.arc.broken": "broken",
    "basis.arc.empty": "no chain",
    "basis.arc.incomplete": "steps missing",
    "basis.arc.malformed-root": "malformed first step",
    "basis.claim.auth-bimi": "BIMI: {state}",
    "basis.bimi.declared": "record and mark location",
    "basis.bimi.partial": "record without a mark location",
    "basis.claim.spoof-verdict": "Sender checks: {worst}",
    "basis.sev.ok": "nothing found",
    "basis.sev.info": "notes only",
    "basis.sev.warn": "warnings",
    "basis.sev.danger": "serious findings",
    "basis.claim.bulk-verdict": "Message kind: {band}, {score} of 100",
    "basis.claim.mailer-fingerprint": "Sending software: {label}",
    "basis.claim.originating-ip": "Originating IP: {ip} ({scope})",
    "basis.claim.threading-message-id": "Message-ID: {id}",
    "basis.part": "Part {id}",
    "basis.bytes": "Bytes {from} to {to}",
    "basis.jump": "Go to header {n}",
    "basis.note":
      "Each line is a claim and the header it was read from. Follow a number to that header.",
    "basis.noneVerified": "Nothing on this list was verified on this device.",
    "verify.group": "Signatures verified here",
    "verify.dkimBody": "DKIM body hash",
    "verify.dkimSig": "DKIM signature",
    "verify.idMismatch": "signer does not match the From address",
    "verify.note":
      "These are computed here from the message bytes, with math, not copied from a relay. A pass is earned. A fail means the signature does not hold. Skipped means the check could not run, often because the public key was not available offline.",
    "dns.group": "Live DNS",
    "dns.run": "Run live DNS checks",
    "dns.again": "Run again",
    "dns.running": "looking up…",
    "dns.none": "no record published",
    "dns.failed": "lookup failed",
    "dns.gate":
      "Turn on Live DNS checks in Settings to look up what this sender's domain publishes.",
    "dns.plan": "When you click, these names go to Cloudflare's public resolver:",
    "dns.note":
      "Each lookup is one request to Cloudflare's public resolver, made only when you click, carrying the name beside it. It sends no cookie and nothing that names this app, but like any request it shows the resolver your device's IP address. It shows what the domain publishes now, so you can hold it against what the relay reported.",
    "dns.pasteGroup": "Record analysis, offline",
    "dns.pasteLead":
      "Paste a DMARC TXT record or CAA lines fetched through a channel you trust; the parsers run here, nothing is looked up.",
    "dns.pastePh": "v=DMARC1; p=reject; rua=mailto:reports@example.com …",
    "dns.pasteAria": "Record to analyse",
    "dns.parseDmarc": "Read as DMARC",
    "dns.parseCaa": "Read as CAA",
    "dns.recValid": "well formed",
    "dns.recIssues": "has issues",
    "sb.title": "Sandbox",
    "sb.isolated": "isolated worker",
    "sb.mainThread": "main thread",
    "sb.skipped": "No risky surface to inspect in this message.",
    "sb.note":
      "What a bounded, isolated worker saw when it opened the risky parts. It runs only in sandboxed mode and never touches the network.",
    "stat.jump": "Jump to",
    "stat.hops": "hops",
    "stat.links": "links",
    "stat.attachments": "attachments",
    "stat.defects": "defects",
    "val.none": "(none)",
    "val.unknown": "(unknown)",
    "val.noDetail": "(no detail)",
    "embed.note":
      "A read-only verdict panel. Inspect a message first, then embed this view.",
    "embed.openInspector": "Open in MIME Smith",
    "embed.openReport": "Open the full report",
    "err.empty": "That file is empty.",
    "err.tooLarge": "That file is larger than {mb} MB. Too big to read in the browser.",
    "err.unreadable": "Couldn't read that file.",
    "err.msgUnreadable": "Couldn't read that Outlook .msg file.",
    "err.pstUnreadable": "Couldn't read that Outlook .pst/.ost archive.",
    "err.pstEmpty": "No messages found in that Outlook archive.",
    "err.zipUnreadable": "That zip archive could not be read.",
    "err.zipEmpty": "No messages found in that zip archive.",
    "err.dropTooLarge":
      "Not opened: this drop is over {mb} MB in total. Drop fewer files at a time.",
    "err.tooManyFiles": "Not opened: this drop is over {n} files. Drop fewer at a time.",
    "err.capReached": "Not opened: the first {n} messages were already filed.",
    "err.archiveTruncated":
      "Filed the first {n} messages from this archive; it holds more. Open it in fewer pieces to see the rest.",
    "err.pasteFirst": "Paste an email or drop a file to inspect.",
    "err.notEmail": "This doesn't look like a valid email.",
    "err.notEmailDetail": "This doesn't look like a valid email: {detail}",
    "err.analysisFailed": "Analysis failed unexpectedly.",
    "rpt.find": "Find in report",
    "rpt.findPlaceholder": "Find in this report…",
    "rpt.findThis": "Find in this report",
    "rpt.closeFind": "Close find",
    "rpt.noMatches": "no matches",
    "rpt.findMinChars": "type at least {n} characters",
    "rpt.findCapped":
      "More matches than this list holds: showing the first {n} of {total}.",
    "rpt.findDropped": "Not listed at all: {cats}.",
    "rpt.findBodyCut":
      "The body is longer than the search reads. Only its first {n} characters were searched, so a match past that is not listed.",
    "find.cat.subject": "Subject",
    "find.cat.preheader": "Preheader",
    "find.cat.header": "Header",
    "find.cat.link": "Link",
    "find.cat.attachment": "Attachment",
    "find.cat.body": "Body",
    "rpt.verdict": "Verdict",
    "rpt.rawView": "Raw view",
    "rpt.rawText": "text",
    "rpt.more": "+ {n} more",
    "auth.empty": "No Authentication-Results headers were present.",
    "auth.splitNote1": "A method can show both a ",
    "auth.splitNote2": " and a ",
    "auth.splitNote3":
      " when the message carries more than one signature, for example the sender's own and a forwarder's. Each signature is verified on its own; the domain on each chip tells you which one it is.",
    "auth.perResult": "Per-result detail: every entry, with the identifiers it cited",
    "arc.group": "ARC chain",
    "arc.saw": "This relay recorded",
    "arc.cvMissing": "not declared",
    "arc.malformedStep": "incomplete seal",
    "arc.valid": "Every one of the {n} steps validated the chain it inherited.",
    "arc.broken":
      "The chain breaks at i={n}. A step sealed after a break cannot repair it, so nothing later in the chain carries the sender's original result forward.",
    "arc.malformedRoot":
      "The first step declares a validation result, but there was no earlier chain for it to validate.",
    "arc.incomplete":
      "The chain skips i={n}. A missing step means the sequence cannot be followed from the sender to here.",
    "arc.reportedNote":
      "Each result was declared by the relay that sealed that step, and is not recomputed here.",
    "dkimh.group": "Signed header coverage",
    "dkimh.unnamed": "signature with no d= tag",
    "dkimh.drift": "Drift",
    "dkimh.missing": "signed, not present",
    "dkimh.overListed": "signed more often than present",
    "dkimh.note":
      "The headers each DKIM signature names in its h= tag. Anything absent from that list is unsigned and can be changed after signing without breaking the signature, which is worth checking for Subject in particular. A name with no header behind it is legal under RFC 6376 section 3.5, and is also what a replayed signature looks like.",
    "prov.group": "Handling providers",
    "prov.cat.esp": "Email service",
    "prov.cat.mailServer": "Mail server",
    "prov.cat.gateway": "Security gateway",
    "prov.cat.helpdesk": "Helpdesk / CRM",
    "prov.field": "{n} field",
    "prov.fields": "{n} fields",
    "prov.noFields": "no fields",
    "prov.note":
      "Recognised from the vendor headers on this message. The headers are stamps a sender can write, so treat them as a lead, not as proof of who handled it.",
    "bimi.group": "Brand indicators",
    "bimi.selector": "Selector",
    "bimi.location": "Mark location",
    "bimi.vmc": "Verified mark certificate",
    "bimi.vmcPresent": "travelled with the message",
    "bimi.vmcAbsent": "none in this message",
    "bimi.note":
      "BIMI decides which logo a mailbox shows next to the sender. The location is stamped by the relay that evaluated the policy, so it is reported here, not checked. The mark itself is never fetched: the URL is shown as text so that reading filed evidence cannot call out to the brand's server. A certificate that travelled with the message is recorded as present and no further: its chain to a mark authority is not walked here, and a half-checked chain shown beside checked signatures would read as more than it is.",
    "rep.meta": "relay-published scores",
    "route.hopsMeta": "{n} hops",
    "route.hopMeta": "{n} hop",
    "route.nonRoutable": "non-routable",
    "route.empty": "No Received headers were found.",
    "route.via": "via {x}",
    "route.clock": "clock",
    "route.resent": "Resent chain",
    "dsig.group": "Delivery notes",
    "dsig.hop": "hop {n}",
    "dsig.privateOrigin":
      "Handed over from a private or loopback address ({ip}), so whatever came before this hop is not visible from outside that network.",
    "dsig.noTls":
      "This relay recorded no TLS cipher for the hop ({protocol}), so it did not claim the message was encrypted in transit here.",
    "dsig.clockBackwards":
      "Stamped {d} earlier than the hop that handed to it. Either a clock is wrong or the chain was written out of order.",
    "dsig.longDelay": "Held {d} before this hop passed the message on.",
    "dsig.duplicateId":
      "Received id {id} appears again here; hop {n} already used it, which a correctly behaving relay does not do.",
    "dsig.note":
      "Read from the Received headers as they were written. Each relay writes its own line, so a note describes what that relay recorded, not what was measured here.",
    "srs.group": "Return-Path rewriting",
    "srs.chipMethod": "srs",
    "srs.forwarder": "Rewritten by",
    "srs.original": "Recovered sender",
    "srs.chain": "Earlier forwarders",
    "srs.note":
      "An {format} envelope sender is a forwarder's rewrite, made so SPF keeps passing after forwarding. The recovered address is decoded from the rewrite, not verified: only the forwarder that wrote it holds the key to check it. Read it as evidence of forwarding, not as proof of who sent the message.",
    "rcpt.group": "Receipt requests",
    "rcpt.read": "read receipt",
    "rcpt.delivery": "delivery receipt",
    "rcpt.offDomain": "not the sender's domain",
    "rcpt.note":
      "The sender asked to be told when this message arrived or was opened. Nothing here sends one: the request is recorded as evidence, so reading the message in MIME Smith cannot report back. A receipt address outside the sender's own domain is worth noting, because it hands a third party the fact that someone opened this.",
    "list.group": "Mailing list",
    "list.id": "List-ID",
    "list.post": "Post to list",
    "list.subscribe": "Subscribe",
    "list.help": "Help",
    "list.archive": "Archive",
    "list.owner": "Owner",
    "list.postNo": "posting not allowed",
    "list.unsub": "Unsubscribe",
    "list.viaUrl": "url",
    "list.viaMail": "mailto",
    "list.oneClick": "one-click",
    "list.notHttps": "not https",
    "list.oneClickNoUrl": "declared without an https address to post to",
    "list.note":
      "List-* headers are written by the list software, and any sender can write the same lines, so read them as a claim about the message rather than proof that a list exists. Nothing here is contacted: every address and URL is shown as text and is never followed. A mailbox turns List-Unsubscribe into a button, and that button is the one control in a message whose purpose is to call back to the sender, so following it from filed evidence would confirm that the address is read. Checking the unsubscribe host is worth the second it costs: a sign-in form behind an unsubscribe link is a known phishing route.",
    "kv.apparentlyTo": "Apparently-To",
    "kv.apparentlyToWhy": "recorded by a relay when the header recipients named nobody",
    "links.meta": "{s} suspicious · {t} trackers",
    "att.metaFiles": "{n} files",
    "att.metaFile": "{n} file",
    "att.metaFlagged": "{n} flagged",
    "att.tnef": "Embedded message",
    "ioc.meta": "{n} extracted",
    "html.meta": "constructs in the HTML body",
    "html.forms": "Forms",
    "html.iframes": "Iframes",
    "html.objects": "Objects / embeds",
    "html.metaRefresh": "Meta refresh",
    "html.inlineHandlers": "Inline handlers",
    "inj.detected": "hidden instructions detected",
    "inj.noted": "patterns noted",
    "uni.meta": "suspicious characters",
    "body.parts": "Parts",
    "body.html": "HTML",
    "body.plain": "plain text",
    "body.lossy": "lossy decode",
    "mime.partsMeta": "{n} parts",
    "mime.partMeta": "{n} part",
    "enc.bytes": "Part bytes",
    "enc.charsets": "Charsets in use",
    "enc.cte": "Transfer encodings",
    "enc.other": "other",
    "enc.group": "Encoding anomalies",
    "enc.tagWarn": "conflict",
    "enc.tagInfo": "note",
    "enc.cte7bit":
      "Declared Content-Transfer-Encoding: {encoding}, but the decoded part contains octets above 127. Either the part is mislabelled, or something was carried through a channel that was told to expect 7-bit text.",
    "enc.charsetNotUtf8":
      "Declared charset {charset}, but the decoded bytes are not valid UTF-8. Text shown for this part may contain replacement characters where the decoder gave up.",
    "enc.note":
      "Read from what each part declares against the bytes that arrived. A conflict is usually a non-conformant sender and occasionally a way to smuggle bytes past a filter that trusted the declaration; it is evidence about the encoding, not a verdict about the message.",
    "hdr.groupMeta": "{n} X-header groups",
    "hdr.groupMetaOne": "{n} X-header group",
    "crypto.meta": "{n} secure parts",
    "crypto.metaOne": "{n} secure part",
    "thr.depth": "depth {n}",
    "thr.software": "Sending software",
    "thr.priority": "Priority",
    "thr.inChain": "{n} in chain",
    "thr.mismatch": "mailer mismatch",
    "thr.mismatch1": " would route through ",
    "thr.mismatch2": ", but the hops show ",
    "thr.mismatchNone": "none of those",
    "def.meta": "{n} found",
    "auto.human": "typed by a person",
    "auto.autoReply": "generated by a machine",
    "auto.outOfOffice": "out-of-office reply",
    "auto.challenge": "list confirmation or challenge",
    "auto.loopRisk": "event notification, mail-loop risk",
    "auto.bulkSuppress": "bulk autoresponder, expects suppression",
    "bulk.personal": "personal",
    "bulk.transactional": "transactional",
    "bulk.bulk": "bulk",
    "bulk.score": "· {n} of 100",
    "bulk.why": "What scored",
  },
  de: {
    "nav.inspect": "Prüfen",
    "nav.triage": "Triage",
    "nav.compare": "Vergleichen",
    "nav.corpus": "Postfach",
    "nav.rfcs": "RFCs",
    "nav.guide": "Handbuch",
    "nav.settings": "Einstellungen",
    "nav.faq": "FAQ",
    "nav.about": "Über",
    "nav.integrations": "Kanäle",
    "nav.roadmap": "Roadmap",
    "nav.impressum": "Impressum",
    "nav.datenschutz": "Datenschutz",
    "app.newFile": "Neue Datei",
    "handoff.from": "Importiert von {source}",
    "handoff.back": "Zurück zur Quelle",
    "handoff.decodeFailed": "Dieser Import-Link konnte nicht dekodiert werden.",
    "app.palette": "Befehlspalette",
    "app.modes": "Modi",
    "app.menu": "Menü",
    "app.menuAria": "Seitenmenü",
    "toc.jump": "Zu einem Abschnitt springen",
    "app.home": "MIME Smith: neu beginnen",
    "app.loadingView": "Diese Ansicht wird geladen",
    "app.reading": "Kopfzeilen werden gelesen",
    "app.reassembling": "Nachricht wird zusammengesetzt…",
    "app.theme": "Darstellung: {mode}. Zum Wechseln klicken.",
    "app.themeTitle": "Darstellung: {mode} (T)",
    "cmd.groupGo": "Gehe zu",
    "cmd.groupJump": "Springen",
    "cmd.groupCompare": "Vergleichen",
    "cmd.compareWith": "Mit {name} vergleichen",
    "cmd.groupSetting": "Einstellung",
    "cmd.hintSection": "Abschnitt",
    "cmd.print": "Bericht drucken",
    "cmd.groupAction": "Aktion",
    "cmd.groupRfc": "RFC",
    "cmd.groupSample": "Beispiel",
    "cmd.hintPage": "Seite",
    "cmd.hintIframe": "iframe",
    "cmd.hintTheme": "Darstellung",
    "cmd.hintSpec": "Spezifikation",
    "cmd.hintLoad": "laden",
    "cmd.inspectNew": "Prüfen: neue Nachricht",
    "cmd.inspectKw": "Start Anfang zurücksetzen",
    "cmd.embed": "Einbetten: schreibgeschützte Einstufungsansicht",
    "cmd.embedKw": "einbetten Widget iframe Panel",
    "cmd.theme": "Darstellung wechseln. Aktuell {mode}",
    "cmd.themeKw": "dunkel hell Erscheinungsbild Design",
    "cmd.sampleKw": "Beispiel Muster Testnachricht",
    "tier.legitimate": "unbedenklich",
    "tier.mixed": "gemischt",
    "tier.suspicious": "verdächtig",
    "tier.malicious": "schädlich",
    "home.eyebrow": "Lokale E-Mail-Forensik · kein Upload",
    "home.title": "Die Spuren einer E-Mail prüfen",
    "home.lede":
      "Öffne eine E-Mail-Datei oder füge ihren Rohquelltext ein. MIME Smith lässt die Originalnachricht unverändert und ordnet Kopfzeilen, Authentifizierung, Zustellweg, Nachrichtenteile, Links und Anhänge direkt auf diesem Gerät ein.",
    "home.choose": "Datei wählen…",
    "home.hint": "zum Prüfen",
    "home.samples": "Beispiele",
    "sec.envelope": "Umschlag",
    "sec.reports": "Berichte",
    "sec.auth": "Authentifizierung",
    "sec.reputation": "Reputation",
    "sec.route": "Zustellweg",
    "sec.links": "Links",
    "sec.attachments": "Anhänge",
    "sec.calendar": "Kalender",
    "cal.event": "Termin",
    "cal.tz": "Zeitzone",
    "cal.organizer": "Organisator",
    "cal.location": "Ort",
    "cal.attendees": "Teilnehmer",
    "cal.conference": "Konferenz-Link",
    "cal.vcards": "Kontaktkarten",
    "cal.orgDiffTag": "Abweichung",
    "cal.orgDiff":
      "Die Organisator-Adresse der Einladung weicht von der Absenderadresse der Nachricht ab.",
    "sec.iocs": "Indikatoren",
    "sec.htmlrisks": "Aktive Inhalte",
    "sec.injection": "Prompt-Injection",
    "sec.unicode": "Unicode-Warnungen",
    "sec.body": "Inhalt",
    "sec.mime": "MIME-Baum",
    "sec.headers": "Kopfzeilen",
    "sec.crypto": "Krypto",
    "sec.sandbox": "Sandbox",
    "sec.threading": "Verkettung",
    "sec.defects": "Mängel",
    "sec.raw": "Rohdaten",
    "set.language": "Sprache",
    "set.languageHint":
      "Übersetzt die Oberfläche; forensische Daten bleiben wie empfangen.",
    "set.theme": "Darstellung",
    "set.contents": "Inhalt",
    "feat.auth": "Authentifizierung",
    "feat.authBody":
      "SPF-, DKIM-, DMARC-, ARC- und BIMI-Befunde nachvollziehen. Gemeldete und lokal geprüfte Ergebnisse bleiben eindeutig getrennt.",
    "feat.route": "Zustellweg",
    "feat.routeBody":
      "Received-Kette, Zeitstempel, Relay-Identitäten und Transportsicherheit in Quellreihenfolge nachvollziehen.",
    "feat.payload": "Nachrichteninhalt",
    "feat.payloadBody":
      "MIME-Teile, gerendertes HTML, Tracking-Referenzen, Links und Anhänge direkt im Browser untersuchen.",
    "kv.from": "Von",
    "kv.to": "An",
    "kv.subject": "Betreff",
    "kv.date": "Datum",
    "kv.size": "Größe",
    "kv.automation": "Automatisierung",
    "kv.contentLang": "Angegebene Sprache",
    "kv.bulk": "Massenversand",
    "kv.campaign": "Kampagne",
    "kv.lookalike": "Ähnliche Namen",
    "kv.originIp": "Ursprungs-IP",
    "kv.charsets": "Zeichensätze",
    "act.next": "Weiter",
    "act.triage": "In Klartext lesen",
    "act.compare": "Mit einer anderen Nachricht vergleichen",
    "act.guide": "Was diese Signale bedeuten",
    "act.iocs": "Indikatoren prüfen",
    "act.dns": "DNS-Prüfungen aktivieren",
    "act.save": "Diese Nachricht merken",
    "act.export": "Ergebnisse exportieren",
    "auth.relayGroup":
      "Vom empfangenden Relay gemeldet, aus den Kopfzeilen kopiert, hier nicht erneut geprüft",
    "auth.localGroup":
      "Auf diesem Gerät geprüft, die lokalen Spoofing-Tests, die MIME Smith selbst durchgeführt hat",
    "set.eyebrow": "Einstellungen, auf diesem Gerät gespeichert",
    "set.lead":
      "In diesem Browser gespeichert. Sie steuern die Darstellung, den Umfang von Exporten und die Grenzen des Parsers. Was das Gerät verlässt, ändern sie nicht.",
    "set.themeHint": "System folgt dem Erscheinungsbild deines Betriebssystems.",
    "set.textSize": "Textgröße",
    "set.textSizeHint": "Skaliert nur die Oberfläche. Messwerte bleiben unverändert.",
    "set.htmlDefault": "HTML-Standardansicht",
    "set.htmlDefaultHint":
      "Quelltext zeigt das Markup wie empfangen; gerendert blockiert externe Inhalte, bis du sie je Nachricht erlaubst.",
    "set.redact": "Exporte schwärzen",
    "set.redactHint": "Entfernt Adressen und Message-IDs aus gespeicherten Berichten.",
    "set.clearOnClose": "Beim Schließen leeren",
    "set.clearOnCloseHint":
      "Verwirft die geladene Nachricht, wenn der Tab geschlossen wird.",
    "set.defaultDest": "Nachrichten öffnen in",
    "set.defaultDestHint":
      "Wo eine frisch geöffnete Datei landet: im vollen Bericht oder in der Triage, dem Blick in Klartext.",
    "set.limitsHead": "Grenzen & Parsing",
    "set.limitsLead":
      "Grenzen für schädliche Eingaben und welche Parser-Stufen laufen. Diese bleiben in diesem Browser erhalten und gelten für die nächste Nachricht, die du öffnest.",
    "set.keyboardHead": "Tastatur",
    "set.devHead": "Entwickler",
    "set.devLead":
      "Deine eigene Liste bekannter schädlicher SHA-256-Hashes. Der Parser markiert jeden Anhang und jeden Indikator, dessen Hash auf dieser Liste steht. In diesem Browser gespeichert, nie hochgeladen.",
    "set.reset": "Darstellung & Datenschutz zurücksetzen",
    "opt.light": "Hell",
    "opt.dark": "Dunkel",
    "opt.system": "System",
    "opt.compact": "Kompakt",
    "opt.normal": "Normal",
    "opt.comfortable": "Komfortabel",
    "opt.rendered": "Gerendert",
    "opt.source": "Quelltext",
    "opt.on": "An",
    "opt.off": "Aus",
    "exp.open": "Exportieren",
    "exp.close": "Exportoptionen ausblenden",
    "exp.groupCopy": "Kopieren",
    "exp.groupDownload": "Speichern",
    "exp.groupPrint": "Drucken",
    "exp.redactNote":
      "Gilt für jeden Export darunter außer Roh kopieren, STIX und Drucken.",
    "exp.unredacted": "nicht geschwärzt",
    "exp.unredactedTitle":
      "Die Schwärzung erreicht diesen Weg nicht. Roh kopieren gibt den Quelltext Byte für Byte heraus, STIX besteht nur aus Indikatoren, und Drucken lässt Abschnitte weg, statt Daten umzuschreiben.",
    "exp.copyRaw": "Roh kopieren",
    "exp.copyJson": "JSON kopieren",
    "exp.downloadJson": "JSON speichern",
    "exp.evidence": "Beweis-ZIP",
    "exp.bundleUnsigned": "unsigniert speichern",
    "exp.bundleUnsignedNote": "hier nicht signierbar",
    "exp.bundleUnsignedToast":
      "Dieser Browser kann das Paket nicht signieren, es wurde daher nicht gespeichert. Nochmals drücken, um es unsigniert und ohne Beweiskette zu übernehmen.",
    "exp.bundleUnsignedTitle":
      "Dieser Browser kann keine Ed25519-Signatur erzeugen, das Paket hätte also keine Beweiskette. Nochmals drücken, um es unsigniert als evidence-bundle-unsigned.zip zu speichern. Ein signiertes Paket liefern aktuelle Versionen von Chrome, Safari oder Firefox.",
    "exp.print": "Drucken",
    "exp.copied": "kopiert",
    "exp.toolbar": "Exportieren",
    "exp.redaction": "Schwärzung",
    "exp.redactNone": "Keine Schwärzung",
    "exp.redactStandard": "Adressen schwärzen",
    "exp.redactStrict": "Strikte Schwärzung",
    "exp.redactTitle":
      "Gilt für jeden Export darunter außer Roh kopieren, STIX und Drucken; strikt ersetzt zusätzlich Dateinamen durch Kennungen und entfernt Inhalte. Der Betreff bleibt in jeder Stufe erhalten: er ist meist der Köder, den die Leserin erkennen soll.",
    "exp.htmlTitle": "Eigenständigen HTML-Bericht speichern",
    "exp.copyFailed": "Kopieren in die Zwischenablage fehlgeschlagen",
    // `speichern` and not `Download`: it is the verb every save button in this
    // product already uses (exp.downloadJson, exp.htmlTitle).
    "exp.downloadBlocked":
      "Der Browser hat das Speichern blockiert, es ist keine Datei entstanden. Das kann an einer Erweiterung oder an einem verlorenen Fensterfokus liegen.",
    "exp.retry": "erneut versuchen",
    "prov.head": "Analyse-Provenienz",
    "exp.printRedact": "geschwärzt drucken",
    "exp.printRedactTitle":
      "Beim Drucken entfallen Zustellweg, Rohquelle und Indikatoren",
    "lib.clear": "leeren",
    "lib.bookmarked": "Lesezeichen",
    "lib.recent": "Zuletzt verwendet",
    "lib.noSubject": "(kein Betreff)",
    "lib.message": "Nachricht {id}",
    "a11y.removeBookmark": "Lesezeichen entfernen",
    "a11y.skip": "Zum Inhalt springen",
    "exp.saved": "gespeichert",
    "exp.cancel": "abbrechen",
    "exp.cancelled": "abgebrochen",
    "exp.cancelTitle": "Erstellen des Pakets stoppen",
    "exp.failed": "fehlgeschlagen",
    "exp.sarifTitle": "SARIF-2.1.0-Befunde",
    "exp.stixTitle": "STIX-2.1-Indikatorpaket",
    "exp.compact": "Kompaktes JSON",
    "exp.compactTitle":
      "Minimiertes JSON ohne dekodierte Inhalte; Anhänge werden zu Blob-Referenzen",
    "exp.groupCsv": "CSV je Bereich",
    "exp.csvTitle": "Diesen Bereich als CSV speichern",
    "exp.csvNote":
      "Eine Datei je Bereich, für die Tabellenkalkulation. Feldkodierung nach RFC 4180, davor ein Herkunftsblock aus Zeilen, die mit # beginnen. Zellen, die als Formel gelesen werden könnten, werden entschärft.",
    "exp.csv.headers": "Kopfzeilen",
    "exp.csv.addresses": "Adressen",
    "exp.csv.links": "Links",
    "exp.csv.attachments": "Anhänge",
    "exp.csv.auth": "Authentifizierung",
    "exp.csv.indicators": "Indikatoren",
    "exp.csv.defects": "Defekte",
    "exp.bundleTitle":
      "Signiertes Beweispaket (.zip): Quelltext, dekodierte Anhänge, JSON, Ed25519-signiertes Manifest. Mit einer Schwärzungsstufe entfallen Quelltext und Anhänge, ihre Prüfsummen bleiben.",
    "bm.add": "merken",
    "bm.added": "gemerkt",
    "bm.addTitle": "Diese Nachricht merken",
    "bm.removeTitle": "Aus der Merkliste entfernen",
    "bm.saveFailedFull":
      "Der Speicher dieses Browsers ist voll, die Nachricht wurde nicht gemerkt. Entferne ein Lesezeichen und versuch es erneut.",
    "bm.saveFailedNoStore":
      "Dieser Browser speichert für diese Seite nichts, die Nachricht wurde nicht gemerkt.",
    "bm.saveFailedRefused":
      "Dieser Browser hat das Speichern abgelehnt, die Nachricht wurde nicht gemerkt.",
    "bm.removeFailed":
      "Das Lesezeichen ließ sich nicht entfernen. Die Nachricht liegt weiterhin in diesem Browser.",
    "vm.head": "Verifizierung",
    "vm.lead":
      "Standardmäßig wird nur ausgewertet, was in der Datei steht. Diese Modi gehen darüber hinaus, jeder mit einem benannten Preis. Sie bleiben aus, bis du einen aktivierst; die Wahl bleibt auf diesem Gerät.",
    "vm.parseOnly": "Nur lesen",
    "vm.parseOnlyDesc":
      "Die Voreinstellung. Nichts verlässt dein Gerät, und es werden keine Signaturen neu berechnet.",
    "vm.dns": "Live-DNS-Prüfungen",
    "vm.dnsDesc":
      "Schlägt SPF, DKIM, DMARC, MX und Reverse-DNS des Absenders beim öffentlichen Resolver von Cloudflare nach, damit ein vom Relay gemeldeter Pass geprüft statt geglaubt werden kann. In die Abfrage legt diese App den Namen: die Domain des Absenders, jeder DKIM-Selector unter der signierenden Domain (oft die eines Dienstleisters, nicht die des Absenders) und die IP-Adresse, von der die Nachricht ausging. Die Anfrage selbst zeigt Cloudflare außerdem deine IP-Adresse, so wie eine Anfrage an jede Website. Der Bericht nennt die genauen Namen, bevor sie abgefragt werden.",
    "vm.crypto": "Signaturen prüfen",
    "vm.cryptoDesc":
      "Berechnet DKIM-Body-Hashes neu und prüft S/MIME- und PGP-Signaturen rechnerisch auf diesem Gerät. Kein Netz.",
    "vm.sandbox": "Anhänge isolieren",
    "vm.sandboxDesc":
      "Untersucht riskante Anhänge (Archive, Dokumente, HTML) in einem begrenzten Worker, abgeschottet von der Seite. Kein Netz.",
    "vm.privNet": "stellt eine Netzanfrage",
    "vm.privCrypto": "rechnet auf dem Gerät",
    "vm.privSandbox": "startet einen Worker",
    "vm.privNone": "bleibt auf dem Gerät",
    "vm.stamp.parse-only": "Nur Parsen, Auth laut Relay",
    "vm.stamp.crypto-enabled": "Offline, Signaturen auf diesem Gerät geprüft",
    "vm.stamp.sandboxed": "Sandbox, kein Netz",
    "vm.stampTitle": "Prüfmodus; änderbar in den Einstellungen",
    "cmd.vmode": "Prüfmodus: {mode}",
    "basis.head": "Worauf das beruht",
    "basis.claim1": "1 Angabe",
    "basis.claimN": "{n} Angaben",
    "basis.kind.raw": "roh",
    "basis.kind.parsed": "aus der Quelle",
    "basis.kind.declared": "laut Relay",
    "basis.kind.inferred": "hier abgeleitet",
    "basis.kind.verified": "hier geprüft",
    "basis.kind.enriched": "angereichert",
    "basis.claim.auth-spf": "SPF: {result}",
    "basis.claim.auth-dkim": "DKIM: {result}",
    "basis.claim.auth-dmarc": "DMARC: {result}",
    "basis.claim.auth-arc-chain": "ARC-Kette: {status}",
    "basis.arc.valid": "gültig",
    "basis.arc.broken": "gebrochen",
    "basis.arc.empty": "keine Kette",
    "basis.arc.incomplete": "Schritte fehlen",
    "basis.arc.malformed-root": "fehlerhafter erster Schritt",
    "basis.claim.auth-bimi": "BIMI: {state}",
    "basis.bimi.declared": "Eintrag mit Ablage der Marke",
    "basis.bimi.partial": "Eintrag ohne Ablage der Marke",
    "basis.claim.spoof-verdict": "Absenderprüfungen: {worst}",
    "basis.sev.ok": "nichts gefunden",
    "basis.sev.info": "nur Hinweise",
    "basis.sev.warn": "Warnungen",
    "basis.sev.danger": "gravierende Funde",
    "basis.claim.bulk-verdict": "Nachrichtenart: {band}, {score} von 100",
    "basis.claim.mailer-fingerprint": "Versandsoftware: {label}",
    "basis.claim.originating-ip": "Ursprungs-IP: {ip} ({scope})",
    "basis.claim.threading-message-id": "Message-ID: {id}",
    "basis.part": "Teil {id}",
    "basis.bytes": "Bytes {from} bis {to}",
    "basis.jump": "Zu Kopfzeile {n}",
    "basis.note":
      "Jede Zeile nennt eine Angabe und die Kopfzeile, aus der sie stammt. Eine Nummer führt zu dieser Kopfzeile.",
    "basis.noneVerified": "Nichts aus dieser Liste wurde auf diesem Gerät geprüft.",
    "verify.group": "Signaturen hier verifiziert",
    "verify.dkimBody": "DKIM-Body-Hash",
    "verify.dkimSig": "DKIM-Signatur",
    "verify.idMismatch": "Unterzeichner passt nicht zur From-Adresse",
    "verify.note":
      "Diese Ergebnisse werden hier aus den Bytes der Nachricht berechnet, rechnerisch, nicht von einem Relay übernommen. Ein Pass ist verdient. Ein Fehlschlag heißt, die Signatur hält nicht. Übersprungen heißt, die Prüfung konnte nicht laufen, oft weil der öffentliche Schlüssel offline nicht verfügbar war.",
    "dns.group": "Live-DNS",
    "dns.run": "Live-DNS-Prüfungen ausführen",
    "dns.again": "Erneut ausführen",
    "dns.running": "wird abgefragt…",
    "dns.none": "kein Eintrag veröffentlicht",
    "dns.failed": "Abfrage fehlgeschlagen",
    "dns.gate":
      "Schalte Live-DNS-Prüfungen in den Einstellungen ein, um nachzusehen, was die Domain dieses Absenders veröffentlicht.",
    "dns.plan":
      "Beim Klick gehen diese Namen an den öffentlichen Resolver von Cloudflare:",
    "dns.note":
      "Jede Abfrage ist eine Anfrage an den öffentlichen Resolver von Cloudflare, nur wenn du klickst, mit dem Namen, der daneben steht. Sie überträgt kein Cookie und keinen Verweis auf diese App, zeigt dem Resolver aber wie jede Anfrage deine IP-Adresse. Sie zeigt, was die Domain jetzt veröffentlicht, damit du es mit dem abgleichen kannst, was das Relay gemeldet hat.",
    "dns.pasteGroup": "Record-Analyse, offline",
    "dns.pasteLead":
      "Füge einen DMARC-TXT-Eintrag oder CAA-Zeilen ein, die du über einen vertrauten Kanal geholt hast; die Parser laufen hier, nichts wird abgefragt.",
    "dns.pastePh": "v=DMARC1; p=reject; rua=mailto:reports@example.com …",
    "dns.pasteAria": "Zu analysierender Eintrag",
    "dns.parseDmarc": "Als DMARC lesen",
    "dns.parseCaa": "Als CAA lesen",
    "dns.recValid": "wohlgeformt",
    "dns.recIssues": "hat Probleme",
    "sb.title": "Sandbox",
    "sb.isolated": "isolierter Worker",
    "sb.mainThread": "Hauptthread",
    "sb.skipped": "Keine riskante Stelle zum Untersuchen in dieser Nachricht.",
    "sb.note":
      "Was ein begrenzter, isolierter Worker gesehen hat, als er die riskanten Teile geöffnet hat. Läuft nur im Sandbox-Modus und berührt nie das Netz.",
    "stat.jump": "Springe zu",
    "stat.hops": "Stationen",
    "stat.links": "Links",
    "stat.attachments": "Anhänge",
    "stat.defects": "Mängel",
    "val.none": "(keine)",
    "val.unknown": "(unbekannt)",
    "val.noDetail": "(keine Angaben)",
    "embed.note":
      "Eine schreibgeschützte Einstufungsansicht. Untersuche zuerst eine Nachricht, dann kannst du diese Ansicht einbetten.",
    "embed.openInspector": "In MIME Smith öffnen",
    "embed.openReport": "Vollständigen Bericht öffnen",
    "err.empty": "Diese Datei ist leer.",
    "err.tooLarge":
      "Diese Datei ist größer als {mb} MB. Zu groß, um sie im Browser zu lesen.",
    "err.unreadable": "Diese Datei ließ sich nicht lesen.",
    "err.msgUnreadable": "Diese Outlook-.msg-Datei ließ sich nicht lesen.",
    "err.pstUnreadable": "Dieses Outlook-.pst/.ost-Archiv ließ sich nicht lesen.",
    "err.pstEmpty": "In diesem Outlook-Archiv wurden keine Nachrichten gefunden.",
    "err.zipUnreadable": "Dieses Zip-Archiv konnte nicht gelesen werden.",
    "err.zipEmpty": "In diesem Zip-Archiv wurden keine Nachrichten gefunden.",
    "err.dropTooLarge":
      "Nicht geöffnet: dieser Stapel ist zusammen größer als {mb} MB. Zieh weniger Dateien auf einmal herein.",
    "err.tooManyFiles":
      "Nicht geöffnet: dieser Stapel umfasst mehr als {n} Dateien. Zieh weniger auf einmal herein.",
    "err.capReached": "Nicht geöffnet: die ersten {n} Nachrichten waren schon abgelegt.",
    "err.archiveTruncated":
      "Die ersten {n} Nachrichten aus diesem Archiv sind abgelegt; es enthält mehr. Öffne es in kleineren Teilen, um den Rest zu sehen.",
    "err.pasteFirst":
      "Füge eine E-Mail ein oder lege eine Datei ab, um sie zu untersuchen.",
    "err.notEmail": "Das sieht nicht nach einer gültigen E-Mail aus.",
    "err.notEmailDetail": "Das sieht nicht nach einer gültigen E-Mail aus: {detail}",
    "err.analysisFailed": "Die Analyse ist unerwartet fehlgeschlagen.",
    "rpt.find": "Im Bericht suchen",
    "rpt.findPlaceholder": "In diesem Bericht suchen…",
    "rpt.findThis": "In diesem Bericht suchen",
    "rpt.closeFind": "Suche schließen",
    "rpt.noMatches": "keine Treffer",
    "rpt.findMinChars": "mindestens {n} Zeichen eingeben",
    "rpt.findCapped":
      "Mehr Treffer, als diese Liste fasst: gezeigt werden die ersten {n} von {total}.",
    "rpt.findDropped": "Gar nicht gelistet: {cats}.",
    "rpt.findBodyCut":
      "Der Nachrichtentext ist länger, als die Suche liest. Durchsucht wurden nur die ersten {n} Zeichen, ein Treffer dahinter fehlt in der Liste.",
    "find.cat.subject": "Betreff",
    "find.cat.preheader": "Preheader",
    "find.cat.header": "Kopfzeile",
    "find.cat.link": "Link",
    "find.cat.attachment": "Anhang",
    "find.cat.body": "Inhalt",
    "rpt.verdict": "Einstufung",
    "rpt.rawView": "Rohansicht",
    "rpt.rawText": "Text",
    "rpt.more": "+ {n} weitere",
    "auth.empty": "Keine Authentication-Results-Kopfzeilen vorhanden.",
    "auth.splitNote1": "Eine Methode kann sowohl einen ",
    "auth.splitNote2": " als auch einen ",
    "auth.splitNote3":
      " zeigen, wenn die Nachricht mehr als eine Signatur trägt, zum Beispiel die des Absenders selbst und die eines Weiterleiters. Jede Signatur wird für sich geprüft; die Domain auf jedem Chip sagt dir, um welche es geht.",
    "auth.perResult": "Detail je Ergebnis: jeder Eintrag mit den genannten Kennungen",
    "arc.group": "ARC-Kette",
    "arc.saw": "Dieses Relay hielt fest",
    "arc.cvMissing": "nicht angegeben",
    "arc.malformedStep": "unvollständiges Siegel",
    "arc.valid": "Jeder der {n} Schritte hat die übernommene Kette geprüft.",
    "arc.broken":
      "Die Kette bricht bei i={n}. Ein Schritt, der nach einem Bruch gesiegelt wurde, kann ihn nicht heilen; deshalb trägt kein späterer Schritt das ursprüngliche Ergebnis des Absenders weiter.",
    "arc.malformedRoot":
      "Der erste Schritt gibt ein Prüfergebnis an, obwohl es keine frühere Kette gab, die er hätte prüfen können.",
    "arc.incomplete":
      "Der Kette fehlt i={n}. Fehlt ein Schritt, lässt sich die Folge vom Absender bis hierher nicht nachvollziehen.",
    "arc.reportedNote":
      "Jedes Ergebnis stammt von dem Relay, das den jeweiligen Schritt gesiegelt hat, und wird hier nicht nachgerechnet.",
    "dkimh.group": "Signierte Kopfzeilen",
    "dkimh.unnamed": "Signatur ohne d=-Tag",
    "dkimh.drift": "Abweichung",
    "dkimh.missing": "signiert, nicht vorhanden",
    "dkimh.overListed": "öfter signiert als vorhanden",
    "dkimh.note":
      "Die Kopfzeilen, die jede DKIM-Signatur in ihrem h=-Tag nennt. Was dort fehlt, ist nicht signiert und lässt sich nach dem Signieren ändern, ohne die Signatur zu brechen; besonders beim Betreff lohnt der Blick. Ein Name ohne zugehörige Kopfzeile ist nach RFC 6376 Abschnitt 3.5 zulässig und sieht zugleich genau so aus wie eine wiederverwendete Signatur.",
    "prov.group": "Beteiligte Anbieter",
    "prov.cat.esp": "E-Mail-Dienst",
    "prov.cat.mailServer": "Mailserver",
    "prov.cat.gateway": "Sicherheits-Gateway",
    "prov.cat.helpdesk": "Helpdesk / CRM",
    "prov.field": "{n} Feld",
    "prov.fields": "{n} Felder",
    "prov.noFields": "keine Felder",
    "prov.note":
      "Erkannt an den Anbieter-Kopfzeilen dieser Nachricht. Diese Kopfzeilen kann ein Absender selbst schreiben; sie sind daher ein Hinweis, kein Nachweis darüber, wer die Nachricht bearbeitet hat.",
    "bimi.group": "Markenkennzeichen",
    "bimi.selector": "Selektor",
    "bimi.location": "Ablage der Marke",
    "bimi.vmcPresent": "kam mit der Nachricht",
    "bimi.vmc": "Geprüftes Markenzertifikat",
    "bimi.vmcAbsent": "in dieser Nachricht keines",
    "bimi.note":
      "BIMI entscheidet, welches Logo ein Postfach neben dem Absender zeigt. Die Ablage stammt von dem Relay, das die Richtlinie ausgewertet hat; sie wird hier also berichtet und nicht geprüft. Die Marke selbst wird nie abgerufen: Die URL steht als Text da, damit das Lesen abgelegter Spuren den Server der Marke nicht anspricht. Ein Zertifikat, das mit der Nachricht kam, wird als vorhanden vermerkt und nicht weiter: Seine Kette zu einer Markenstelle wird hier nicht verfolgt, und eine halb geprüfte Kette neben geprüften Signaturen läse sich als mehr, als sie ist.",
    "rep.meta": "vom Relay veröffentlichte Werte",
    "route.hopsMeta": "{n} Stationen",
    "route.hopMeta": "{n} Station",
    "route.nonRoutable": "nicht routbar",
    "route.empty": "Keine Received-Kopfzeilen gefunden.",
    "route.via": "über {x}",
    "route.clock": "Uhr",
    "route.resent": "Resent-Kette",
    "dsig.group": "Zustellhinweise",
    "dsig.hop": "Station {n}",
    "dsig.privateOrigin":
      "Übergabe von einer privaten oder Loopback-Adresse ({ip}). Was vor dieser Station lag, ist von außerhalb dieses Netzes nicht sichtbar.",
    "dsig.noTls":
      "Dieses Relay hat für die Station keine TLS-Verschlüsselung vermerkt ({protocol}); es behauptet also nicht, die Nachricht sei hier verschlüsselt übertragen worden.",
    "dsig.clockBackwards":
      "Um {d} früher gestempelt als die Station, die übergeben hat. Entweder geht eine Uhr falsch oder die Kette wurde in falscher Reihenfolge geschrieben.",
    "dsig.longDelay": "Blieb {d} liegen, bevor diese Station weitergegeben hat.",
    "dsig.duplicateId":
      "Die Received-Kennung {id} taucht hier erneut auf; Station {n} hat sie bereits verwendet, was ein korrekt arbeitendes Relay nicht tut.",
    "dsig.note":
      "Aus den Received-Kopfzeilen gelesen, so wie sie geschrieben wurden. Jedes Relay schreibt seine eigene Zeile; ein Hinweis beschreibt also, was dieses Relay vermerkt hat, nicht was hier gemessen wurde.",
    "srs.group": "Return-Path-Umschreibung",
    "srs.chipMethod": "srs",
    "srs.forwarder": "Umgeschrieben von",
    "srs.original": "Ermittelter Absender",
    "srs.chain": "Frühere Weiterleiter",
    "srs.note":
      "Ein {format}-Umschlagabsender ist die Umschreibung eines Weiterleiters, damit SPF nach der Weiterleitung weiter besteht. Die ermittelte Adresse wird aus dieser Umschreibung dekodiert, nicht geprüft: nur der Weiterleiter, der sie geschrieben hat, besitzt den Schlüssel dazu. Sie belegt eine Weiterleitung, nicht, wer die Nachricht gesendet hat.",
    "rcpt.group": "Empfangsbestätigungen",
    "rcpt.read": "Lesebestätigung",
    "rcpt.delivery": "Zustellbestätigung",
    "rcpt.offDomain": "nicht die Domain des Absenders",
    "rcpt.note":
      "Der Absender hat darum gebeten, über Zustellung oder Öffnen der Nachricht benachrichtigt zu werden. Hier wird nichts davon gesendet: die Anforderung wird als Spur festgehalten, das Lesen in MIME Smith meldet also nichts zurück. Eine Bestätigungsadresse außerhalb der Absenderdomain ist bemerkenswert, denn sie verrät einem Dritten, dass jemand diese Nachricht geöffnet hat.",
    "list.group": "Mailingliste",
    "list.id": "List-ID",
    "list.post": "An die Liste senden",
    "list.subscribe": "Anmelden",
    "list.help": "Hilfe",
    "list.archive": "Archiv",
    "list.owner": "Betreiber",
    "list.postNo": "Senden nicht erlaubt",
    "list.unsub": "Abmeldung",
    "list.viaUrl": "url",
    "list.viaMail": "mailto",
    "list.oneClick": "Ein-Klick",
    "list.notHttps": "kein https",
    "list.oneClickNoUrl": "angegeben, aber ohne https-Adresse zum Absenden",
    "list.note":
      "List-*-Kopfzeilen schreibt die Listensoftware, und jeder Absender kann dieselben Zeilen schreiben; sie sind daher eine Angabe über die Nachricht und kein Nachweis, dass es die Liste gibt. Hier wird nichts davon angesprochen: Jede Adresse und jede URL steht als Text da und wird nie aufgerufen. Ein Postfach macht aus List-Unsubscribe eine Schaltfläche, und diese Schaltfläche ist die eine Bedienung einer Nachricht, deren Zweck der Rückruf zum Absender ist; ein Aufruf aus abgelegten Spuren heraus bestätigte dem Absender also, dass die Adresse gelesen wird. Ein Blick auf den Host der Abmeldeadresse lohnt sich: Ein Anmeldeformular hinter einem Abmeldelink ist ein bekannter Phishing-Weg.",
    "kv.apparentlyTo": "Apparently-To",
    "kv.apparentlyToWhy":
      "von einem Relay vermerkt, weil die Kopfzeilen keinen Empfänger nannten",
    "links.meta": "{s} verdächtig · {t} Tracker",
    "att.metaFiles": "{n} Dateien",
    "att.metaFile": "{n} Datei",
    "att.metaFlagged": "{n} markiert",
    "att.tnef": "Eingebettete Nachricht",
    "ioc.meta": "{n} extrahiert",
    "html.meta": "Konstrukte im HTML-Inhalt",
    "html.forms": "Formulare",
    "html.iframes": "Iframes",
    "html.objects": "Objekte / Embeds",
    "html.metaRefresh": "Meta-Refresh",
    "html.inlineHandlers": "Inline-Handler",
    "inj.detected": "versteckte Anweisungen erkannt",
    "inj.noted": "Muster vermerkt",
    "uni.meta": "verdächtige Zeichen",
    "body.parts": "Teile",
    "body.html": "HTML",
    "body.plain": "Klartext",
    "body.lossy": "verlustbehaftete Dekodierung",
    "mime.partsMeta": "{n} Teile",
    "mime.partMeta": "{n} Teil",
    "enc.bytes": "Bytes je Art",
    "enc.charsets": "Verwendete Zeichensätze",
    "enc.cte": "Übertragungskodierungen",
    "enc.other": "sonstige",
    "enc.group": "Kodierungswidersprüche",
    "enc.tagWarn": "Widerspruch",
    "enc.tagInfo": "Hinweis",
    "enc.cte7bit":
      "Angegeben ist Content-Transfer-Encoding: {encoding}, der dekodierte Teil enthält jedoch Oktette über 127. Entweder ist der Teil falsch ausgezeichnet, oder es wurde etwas über einen Weg geschickt, der reinen 7-Bit-Text erwartet hat.",
    "enc.charsetNotUtf8":
      "Angegeben ist der Zeichensatz {charset}, die dekodierten Bytes sind aber kein gültiges UTF-8. Im Text dieses Teils können Ersatzzeichen stehen, wo der Dekodierer aufgegeben hat.",
    "enc.note":
      "Gelesen wird, was jeder Teil angibt, gegen die Bytes, die angekommen sind. Ein Widerspruch geht meist auf einen nicht regelkonformen Absender zurück und gelegentlich darauf, Bytes an einem Filter vorbeizuschleusen, der der Angabe vertraut hat; er ist ein Nachweis über die Kodierung, keine Einstufung der Nachricht.",
    "hdr.groupMeta": "{n} X-Header-Gruppen",
    "hdr.groupMetaOne": "{n} X-Header-Gruppe",
    "crypto.meta": "{n} sichere Teile",
    "crypto.metaOne": "{n} sicherer Teil",
    "thr.depth": "Tiefe {n}",
    "thr.software": "Sendende Software",
    "thr.priority": "Priorität",
    "thr.inChain": "{n} in der Kette",
    "thr.mismatch": "Mailer-Abweichung",
    "thr.mismatch1": " würde über ",
    "thr.mismatch2": " laufen, aber die Stationen zeigen ",
    "thr.mismatchNone": "keine davon",
    "def.meta": "{n} gefunden",
    "auto.human": "von einer Person geschrieben",
    "auto.autoReply": "maschinell erzeugt",
    "auto.outOfOffice": "Abwesenheitsantwort",
    "auto.challenge": "Listenbestätigung oder Rückfrage",
    "auto.loopRisk": "Ereignismeldung, Gefahr einer Mailschleife",
    "auto.bulkSuppress": "Massen-Autoresponder, erwartet Unterdrückung",
    "bulk.personal": "persönlich",
    "bulk.transactional": "transaktional",
    "bulk.bulk": "Massenversand",
    "bulk.score": "· {n} von 100",
    "bulk.why": "Was gezählt hat",
  },
};

function resolve(pref: LocalePref): Locale {
  if (pref !== "system") return pref;
  const nav =
    typeof navigator !== "undefined" && navigator.language
      ? navigator.language.toLowerCase()
      : "en";
  if (nav.startsWith("de")) return "de";
  return "en";
}

interface Ctx {
  locale: Locale;
  pref: LocalePref;
  setPref: (p: LocalePref) => void;
  t: (key: string, fallback?: string) => string;
}

const LocaleCtx = createContext<Ctx | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<LocalePref>(() => {
    try {
      // Namespaced key first, then the legacy key (migrated to the new key
      // and removed so the storage sweep cannot miss it).
      const v =
        localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY);
      if (v === "en" || v === "de" || v === "system") {
        if (localStorage.getItem(STORAGE_KEY) == null) {
          localStorage.setItem(STORAGE_KEY, v);
          localStorage.removeItem(LEGACY_STORAGE_KEY);
        }
        return v;
      }
    } catch {
      /* storage unavailable */
    }
    return "system";
  });
  const locale = resolve(pref);

  useEffect(() => {
    try {
      document.documentElement.lang = locale;
    } catch {
      /* no document */
    }
  }, [locale]);

  const setPref = (p: LocalePref) => {
    setPrefState(p);
    try {
      localStorage.setItem(STORAGE_KEY, p);
    } catch {
      /* storage unavailable */
    }
  };

  const t = (key: string, fallback?: string) =>
    STRINGS[locale][key] ?? STRINGS.en[key] ?? fallback ?? key;

  return (
    <LocaleCtx.Provider value={{ locale, pref, setPref, t }}>
      {children}
    </LocaleCtx.Provider>
  );
}

export function useT(): Ctx {
  const ctx = useContext(LocaleCtx);
  if (ctx) return ctx;
  // Fallback when used outside the provider (tests, isolated renders).
  return {
    locale: "en",
    pref: "system",
    setPref: () => {},
    t: (key: string, fallback?: string) => STRINGS.en[key] ?? fallback ?? key,
  };
}
